import React from 'react'
import { motion } from 'framer-motion'
import { Calendar, ChevronRight, MapPin } from 'lucide-react'
import Container from './Container'
import SimaLeadForm from './SimaLeadForm'
import WaveDivider from './WaveDivider'

const HeroSima: React.FC = () => {
  return (
    <section className="relative min-h-[90vh] pt-20 md:pt-24 pb-20 md:pb-32 flex items-center bg-primary overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="/assets/bavarian-hero.jpg"
          alt="Bavarian Real Estate Punta Cana"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary via-primary/95 to-primary/80"></div>
      </div>

      <Container size="xl" className="relative z-10 pt-4 md:pt-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-8 items-center">
          
          {/* Left Column - Content */}
          <div className="lg:col-span-7 text-white order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-[11px] font-bold uppercase tracking-widest backdrop-blur-md mb-6 md:mb-8 border border-white/10 shadow-xl">
                <span className="flex h-2 w-2 rounded-full bg-accent-light animate-pulse"></span>
                SIMA 2026 - Madrid
              </div>
              
              <h1 className="mb-5 md:mb-6 font-display text-[2.2rem] md:text-5xl lg:text-7xl font-semibold leading-[1.05] tracking-tight text-balance">
                Bavarian Real Estate en SIMA 2026
              </h1>
              
              <p className="mb-8 md:mb-10 text-base md:text-xl text-secondary-light/90 max-w-2xl text-balance font-light leading-relaxed">
                Conoce oportunidades inmobiliarias exclusivas en Punta Cana y agenda una cita privada con nuestros expertos en IFEMA.
              </p>

              <ul className="mb-8 md:mb-12 space-y-4 md:space-y-5">
                {[
                  'Reuniones privadas y orientación clara (sin presión)',
                  'Proyectos destacados: ORIÓN y FOREST',
                  'Propiedades desde US$125,000 (según disponibilidad)',
                ].map((bullet, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-accent text-white shadow-lg shadow-accent/20">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-secondary-light text-base md:text-lg font-light">{bullet}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 md:mb-10">
                <a
                  href="#agenda"
                  className="inline-flex items-center justify-center rounded-lg bg-accent px-6 md:px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-accent-dark hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(2,132,199,0.5)]"
                >
                  <Calendar className="mr-3 h-5 w-5" />
                  Agendar Cita
                </a>
                <a
                  href="#proyectos"
                  className="inline-flex items-center justify-center rounded-lg border border-white/20 bg-white/5 backdrop-blur-md px-6 md:px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-white/10 hover:border-white/30"
                >
                  Ver Proyectos
                  <ChevronRight className="ml-2 h-5 w-5 btn-arrow" />
                </a>
              </div>

              {/* Microcopy */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-secondary-light/80 border-t border-white/10 pt-5 md:pt-6">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 opacity-70" />
                  <span>20–23 mayo 2026</span>
                </div>
                <span className="hidden sm:inline opacity-50">•</span>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 opacity-70" />
                  <span>IFEMA Madrid</span>
                </div>
                <span className="hidden sm:inline opacity-50">•</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-accent-light">Cupos por agenda</span>
                </div>
              </div>

            </motion.div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-5 order-1 lg:order-2" id="agenda">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="lg:pl-8 xl:pl-12"
            >
              <SimaLeadForm id="hero" />
            </motion.div>
          </div>

        </div>
      </Container>

      {/* Wave bottom separator */}
      <WaveDivider color="#FFFFFF" />
    </section>
  )
}

// Simple Check Icon
function Check(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

export default HeroSima
